import * as React from 'react';
import tw from 'twin.macro';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'next-i18next';

import { Alert, TextInput } from '@restworld/ui-ds';

import { RestaurantOnboardingPayload } from './restaurant-onboarding-form';
import { useEmailSmartValidation, validateEmailFormat } from './use-email-smart-validation';

interface EmailFieldWithSuggestionsProps {
  label: string;
  required: string;
}

const EmailFieldWithSuggestions: React.FC<EmailFieldWithSuggestionsProps> = ({ label, required }) => {
  const {
    control,
    formState: { errors },
    setValue,
    trigger,
  } = useFormContext<RestaurantOnboardingPayload>();
  const { t } = useTranslation('restaurant-onboarding');

  const { suggestion, mxWarning, isCheckingMx, applySuggestion, dismissSuggestion, dismissMxWarning, runChecks } =
    useEmailSmartValidation(setValue, trigger);

  return (
    <div tw="self-stretch inline-flex flex-col justify-start items-start gap-2">
      <h5 tw="justify-center text-neutral-700 text-base font-medium">{label}</h5>

      <Controller
        control={control}
        name="email"
        rules={{
          required,
          validate: (value) => {
            const errorKey = validateEmailFormat(value);
            return errorKey ? t(errorKey) : true;
          },
        }}
        render={({ field }) => (
          <div
            tw="w-full"
            onBlur={() => {
              field.onBlur();
              runChecks(field.value || '');
            }}
          >
            <TextInput
              {...field}
              value={field.value || ''}
              placeholder=""
              id="email"
              {...{ bordercolor: 'neutral-90', twStyle: tw`w-full` }}
              type="email"
              supportText={errors.email?.message}
              isError={!!errors.email}
            />
          </div>
        )}
      />

      {/* Mailcheck typo suggestion */}
      {suggestion && (
        <Alert
          variant="info"
          wrapperStyling={tw`w-full`}
          textStyling={tw`w-full`}
          text={
            <span tw="flex items-center justify-between gap-2 w-full">
              <span>{t('email_did_you_mean', { suggestion: suggestion.full })}</span>
              <span tw="flex items-center gap-3 shrink-0">
                <button type="button" onClick={applySuggestion} tw="font-semibold underline cursor-pointer">
                  {t('email_fix_suggestion')}
                </button>
                <button type="button" onClick={dismissSuggestion} tw="opacity-50 cursor-pointer">
                  ✕
                </button>
              </span>
            </span>
          }
        />
      )}

      {/* MX record warning */}
      {mxWarning && !suggestion && (
        <Alert
          variant="info"
          wrapperStyling={tw`w-full`}
          textStyling={tw`w-full`}
          text={
            <span tw="flex items-center justify-between gap-2 w-full">
              <span>{t('email_mx_warning', { domain: mxWarning })}</span>
              <button type="button" onClick={dismissMxWarning} tw="opacity-50 cursor-pointer shrink-0">
                ✕
              </button>
            </span>
          }
        />
      )}

      {/* MX check loading indicator */}
      {isCheckingMx && (
        <p tw="text-neutral-300 text-sm">{t('email_checking_domain') || 'Verifica dominio in corso...'}</p>
      )}

      <p tw="text-neutral-300">*{t('email_notification_hint') || 'Ti avviseremo qui quando ricevi candidature'}</p>
    </div>
  );
};

export default EmailFieldWithSuggestions;
